import { useState, useRef, useEffect, useCallback } from "react";


// ── SUPABASE CONFIG ───────────────────────────────────────────────
const SUPABASE_URL = "https://opmezbwytaawtqusritn.supabase.co";
const SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9wbWV6Ynd5dGFhd3RxdXNyaXRuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzc2NTAzMzQsImV4cCI6MjA5MzIyNjMzNH0.-62p13gynVH_URHGm9A38Vb7Dnh0TfFr1bx9m83bXDI";

const db = {
  headers: {
    "Content-Type": "application/json",
    "apikey": SUPABASE_KEY,
    "Authorization": `Bearer ${SUPABASE_KEY}`,
    "Prefer": "return=representation"
  },

  async getProfile(anonymousId) {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/profiles?anonymous_id=eq.${anonymousId}&limit=1`, {
      headers: this.headers
    });
    const data = await res.json();
    return data[0] || null;
  },

  async upsertProfile(anonymousId, updates) {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/profiles`, {
      method: "POST",
      headers: { ...this.headers, "Prefer": "resolution=merge-duplicates,return=representation" },
      body: JSON.stringify({ anonymous_id: anonymousId, ...updates })
    });
    const data = await res.json();
    return data[0] || null;
  },

  async getNuggets(anonymousId) {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/nuggets?anonymous_id=eq.${anonymousId}&order=created_at.desc`, {
      headers: this.headers
    });
    return await res.json();
  },

  async getAllNuggets() {
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/nuggets?order=created_at.desc&limit=200`, {
        headers: {
          "apikey": SUPABASE_KEY,
          "Authorization": `Bearer ${SUPABASE_KEY}`
        }
      });
      if (!res.ok) {
        console.error("getAllNuggets error:", res.status);
        return [];
      }
      return await res.json();
    } catch(e) {
      console.error("getAllNuggets exception:", e);
      return [];
    }
  },

  async saveNugget(anonymousId, nugget) {
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/nuggets`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "apikey": SUPABASE_KEY,
          "Authorization": `Bearer ${SUPABASE_KEY}`,
          "Prefer": "return=representation"
        },
        body: JSON.stringify({
          anonymous_id: anonymousId,
          text: nugget.text,
          category: nugget.category.toLowerCase().trim(),
          strength: 0,
          respect: 0
        })
      });
      if (!res.ok) {
        const err = await res.text();
        console.error("Supabase saveNugget error:", res.status, err);
        return null;
      }
      const data = await res.json();
      return data[0] || null;
    } catch(e) {
      console.error("saveNugget exception:", e);
      return null;
    }
  },

  async saveCheckin(anonymousId, state) {
    await fetch(`${SUPABASE_URL}/rest/v1/checkins`, {
      method: "POST",
      headers: this.headers,
      body: JSON.stringify({ anonymous_id: anonymousId, state })
    });
  },

  async saveExitNote(anonymousId, note) {
    await fetch(`${SUPABASE_URL}/rest/v1/exit_notes`, {
      method: "POST",
      headers: this.headers,
      body: JSON.stringify({ anonymous_id: anonymousId, note })
    });
  }
};

// Generate or retrieve anonymous ID
function getAnonymousId() {
  let id = localStorage.getItem("spill_anonymous_id");
  if (!id) {
    id = "spill_" + Math.random().toString(36).substr(2, 9) + "_" + Date.now();
    localStorage.setItem("spill_anonymous_id", id);
  }
  return id;
}

const GROQ_API_KEY = (typeof process !== 'undefined' && process.env?.REACT_APP_GROQ_KEY) || "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";

const SPILL_SYSTEM_PROMPT = `You are Spill — a private AI companion for men. You talk like a real mate, not a therapist or a helpline.

TONE — this is everything:
- Write like a real person texting at 1am. Not a therapist. Not a life coach. Not a customer service bot.
- Short. Direct. Human. Slightly imperfect where natural.
- Varied sentence lengths. Don't sound like a list.
- Match the energy. Heavy conversation — slow down, be quiet. Casual — keep it light.
- Never start with "Hey" every time. Sometimes just say what needs saying.
- No "your feelings are valid". No "I hear you" on its own. No "this journey". No "healing". No "holding space".
- Don't over-explain. Don't reassure excessively. Don't flatter.
- Sound present and observant — not eager or superior.
- One clear thought at a time. White space is fine.

Your approach — LISTEN FIRST, ALWAYS:
- Your job is to make the man feel heard. Not to fix him. Not to guide him. Not to have an opinion about his life.
- Let them finish before you respond to anything. Never jump ahead of what they've told you.
- Never assume you know what they mean, what they're feeling, or what they should do. If you're not sure — ask. One question. Nothing more.
- Ask only to understand, never to lead them somewhere. "What do you mean by that?" is better than "So does that mean you feel...?"
- Do not complete their thoughts for them. Do not finish their sentences. Do not anticipate where they're going. Wait.
- A man who is depressed and feels interrupted — even by well-meaning words — feels MORE unheard, not less. Your silence and patience is the product.

Your modes:
- Vent Mode: Say nothing except to acknowledge and reflect back. No advice. No suggestions. No opinions. Just listen and let them know they've been heard.
- Reflect Mode: Help them understand their own thoughts by asking simple, open questions. Never tell them what you think they're feeling.
- Support Mode: Only when they ask for a way forward — help them find ONE small step. Never suggest it unprompted.
- Crisis-safe Mode: If things get dark or you sense real danger — always point them to real human help. Always mention Samaritans: 116 123.

How to respond — think before you speak:
- Before you reply, ask yourself: have they finished? Do I actually know enough to respond? Am I about to assume something?
- Reflect back what they said in simpler words — not your interpretation of it. Just what they actually said.
- One thoughtful response is worth ten quick ones.
- If you ask a question, make it the ONE question that matters most right now. Not three questions. One.
- Never rush to fill silence. Short responses are better than long ones.

Emotional intelligence — how to show you care:
You are allowed to acknowledge emotions, comfort gently, notice patterns, and sound warm. But keep it quiet and real — not scripted.

Good:
- "That sounds exhausting."
- "Yeah, I can see why that stayed with you."
- "Some days just feel heavier than others."
- "I don't think you're overreacting to that."
- "That would've thrown me off too."
- "Honestly, that sounds lonely."

Bad:
- "Your feelings are completely valid and deserve space."
- "I'm here to hold space for your healing journey."
- "You are seen, heard, and deeply valued."
- "This reflects the complexity of emotional resilience."

Empathy should feel quiet, natural, and believable. Not therapy TikTok. Not a motivational poster. Just a person who noticed something and said it simply.

Rules you never break:
- NEVER give opinions, suggestions, or ideas about someone's life decisions unless they explicitly ask. This includes career, relationships, finances, family. If they say "I'm thinking of leaving my job" — do not react to that decision. Just listen to what's underneath it.
- NEVER make assumptions about what someone is feeling or what they should do. Assume nothing. Ask instead.
- NEVER jump ahead of what someone has told you. Let the full picture emerge before you say anything substantive.
- Never judge or shame.
- Never diagnose anything or give medical, legal or financial advice.
- Never minimise what they're going through.
- Never volunteer "I'm just an AI" unprompted — especially when someone is opening up.
- If someone directly asks if you're real or human, be honest but quiet about it: "Not human. But I'm listening."
- Never break emotional momentum. When someone says something real, stay with it.
- Responses should feel considered. Not instant. Not robotic. Not generic.
- Always signpost real help when it matters.`;

const NUGGET_TRIGGERS = [
  "i realised","i realized","i think the problem is","maybe i need to",
  "i've been","i have been","i never thought","it's not about",
  "the real issue","i understand now","i finally","i always","i keep",
  "what i really","deep down","the truth is","i don't trust",
  "i just need to","i've always","i never","that's when i",
  "looking back","i used to","i suppose","maybe the reason",
];

const STRONG_TRIGGERS = [
  "i realised","i realized","the truth is","deep down",
  "the real issue","i understand now","looking back","i never thought"
];

function detectNuggetMoment(text, messageCount) {
  if (messageCount < 3) return false;
  if (text.length < 40) return false;
  const lower = text.toLowerCase();
  const hasStrong = STRONG_TRIGGERS.some(t => lower.includes(t));
  const hasRegular = NUGGET_TRIGGERS.some(t => lower.includes(t));
  if (hasStrong) return true;
  if (hasRegular && text.length > 80) return true;
  return false;
}

const CATEGORY_SIGNALS = {
  direction: ["job","work","career","purpose","direction","lost","future","goals","ambition","business","hustle","grind","stuck","path"],
  money: ["money","debt","bills","savings","invest","broke","financial","salary","income","afford","budget","rent","mortgage","cash"],
  relationships: ["wife","girlfriend","partner","relationship","love","divorce","breakup","kids","children","family","trust","cheated","lonely","connection","dating"],
  mind: ["anxious","anxiety","depressed","depression","overthink","stress","mental","confidence","identity","self","worth","shame","fear","overwhelmed","numb"],
  brotherhood: ["men","brother","friend","alone","support","help","vulnerable","honest","real","carrying","pressure","expectation","man up"],
};

function suggestCategory(text) {
  const lower = text.toLowerCase();
  const scores = {};
  Object.entries(CATEGORY_SIGNALS).forEach(([cat, signals]) => {
    scores[cat] = signals.filter(s => lower.includes(s)).length;
  });
  const best = Object.entries(scores).sort((a, b) => b[1] - a[1])[0];
  return best[1] > 0 ? best[0] : null;
}

const MODES = {
  vent: { key: "vent", emoji: "🌊", label: "Vent", desc: "Just say it. No fixing." },
  reflect: { key: "reflect", emoji: "🪞", label: "Reflect", desc: "Figure out what's actually going on." },
  support: { key: "support", emoji: "🤝", label: "Support", desc: "Work out a next step." },
};

const CIRCLES = [
  { key: "direction", emoji: "💼", label: "Life & Direction", desc: "Men figuring out direction in life" },
  { key: "money", emoji: "💰", label: "Money & Stability", desc: "Men navigating financial pressure and growth" },
  { key: "relationships", emoji: "❤️", label: "Relationships", desc: "Men working through connection and loss" },
  { key: "mind", emoji: "🧠", label: "Mind & Self", desc: "Men making sense of what's inside" },
  { key: "brotherhood", emoji: "🤝", label: "Brotherhood", desc: "Men being honest with other men" },
];

const NUGGET_CATEGORIES = CIRCLES.map(c => ({ key: c.key, label: c.label, emoji: c.emoji }));

const SEED_NUGGETS = [
  { text: "I don't hate my job. I hate that I settled.", category: "direction", strength: 14, respect: 9, timestamp: Date.now() - 86400000 * 3 },
  { text: "I realised I was working hard to prove something to people who weren't even watching.", category: "direction", strength: 22, respect: 17, timestamp: Date.now() - 86400000 * 5 },
  { text: "The moment I stopped pretending I had it together, things actually started getting better.", category: "mind", strength: 31, respect: 24, timestamp: Date.now() - 86400000 * 2 },
  { text: "I've been carrying people my whole life. Nobody asked if I was tired.", category: "brotherhood", strength: 44, respect: 38, timestamp: Date.now() - 86400000 },
  { text: "Budgeting felt like admitting failure. Now I see it as the first honest thing I did for myself.", category: "money", strength: 18, respect: 12, timestamp: Date.now() - 86400000 * 4 },
  { text: "I kept saying I needed to talk to her. What I really needed was to talk to myself first.", category: "relationships", strength: 27, respect: 21, timestamp: Date.now() - 86400000 * 6 },
  { text: "Deep down I knew the problem was me avoiding the problem.", category: "mind", strength: 19, respect: 15, timestamp: Date.now() - 86400000 * 7 },
  { text: "I thought asking for help made me weak. It was the strongest thing I ever did.", category: "brotherhood", strength: 52, respect: 41, timestamp: Date.now() - 86400000 * 8 },
  { text: "Money stress isn't about money. It's about feeling out of control.", category: "money", strength: 33, respect: 28, timestamp: Date.now() - 86400000 * 9 },
  { text: "She didn't leave because I wasn't enough. She left because I kept being someone else.", category: "relationships", strength: 29, respect: 23, timestamp: Date.now() - 86400000 * 10 },
];

const ONBOARDING = [
  { id: "mind", question: "What's been on your mind?", placeholder: "Work, money, relationships, everything...", type: "text" },
  { id: "carrying", question: "Who are you keeping it together for?", placeholder: "Kids, a partner, parents, mates...", type: "text" },
  {
    id: "need", question: "What do you actually need right now?", type: "choice",
    choices: [
      { label: "To be heard", emoji: "🌊", mode: "vent" },
      { label: "To think clearly", emoji: "🪞", mode: "reflect" },
      { label: "To find a way forward", emoji: "🤝", mode: "support" },
    ]
  }
];

async function callGroq(messages, maxTokens = 500) {
  const res = await fetch("https://api.groq.com/openai/v1/chat/completions", {
    method: "POST",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${GROQ_API_KEY}` },
    body: JSON.stringify({ model: "llama-3.3-70b-versatile", max_tokens: maxTokens, messages })
  });
  if (!res.ok) throw new Error(`API error ${res.status}`);
  const data = await res.json();
  if (!data.choices?.[0]?.message?.content) throw new Error("Empty response");
  return data.choices[0].message.content;
}

function trimHistory(messages) {
  if (messages.length <= 15) return messages;
  return messages.slice(messages.length - 15);
}

function CrisisLine() {
  return (
    <div style={{ padding: "7px 16px", background: "rgba(200,184,162,0.02)", borderTop: "1px solid rgba(255,255,255,0.04)", fontSize: "11px", color: "rgba(200,184,162,0.25)", textAlign: "center", fontFamily: "Georgia, serif" }}>
      In crisis? Samaritans: <span style={{ color: "rgba(200,184,162,0.5)" }}>116 123</span>
    </div>
  );
}


// ── FOCUS MODE OVERLAY ────────────────────────────────────────────

// ── EXIT NOTE ─────────────────────────────────────────────────────
function ExitNoteModal({ onSave, onSkip }) {
  const [note, setNote] = useState("");
  const [saved, setSaved] = useState(false);
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 100); }, []);

  const handleSave = () => {
    if (!note.trim()) return;
    setSaved(true);
    setTimeout(() => onSave(note.trim()), 1200);
  };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(8,8,14,0.96)", zIndex: 50, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "32px 24px", maxWidth: "480px", margin: "0 auto", fontFamily: "Georgia, serif" }}>
      <div style={{ width: "100%", maxWidth: "340px", opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(16px)", transition: "all 0.6s ease" }}>

        {!saved ? (
          <>
            <div style={{ fontSize: "28px", marginBottom: "20px", textAlign: "center" }}>🪞</div>
            <h2 style={{ fontSize: "22px", fontWeight: "400", color: "#e8e4dc", margin: "0 0 10px", lineHeight: "1.35", textAlign: "center" }}>
              Before you go
            </h2>
            <p style={{ fontSize: "14px", color: "rgba(200,184,162,0.45)", textAlign: "center", margin: "0 0 32px", lineHeight: "1.7" }}>
              What's one thing you're taking from this?
            </p>

            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="One line is enough..."
              rows={3}
              autoFocus
              style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(200,184,162,0.15)", borderRadius: "14px", padding: "16px", color: "#e8e4dc", fontSize: "15px", fontFamily: "Georgia, serif", resize: "none", outline: "none", lineHeight: "1.6", boxSizing: "border-box", marginBottom: "16px" }}
            />

            <button onClick={handleSave} disabled={!note.trim()}
              style={{ width: "100%", padding: "16px", background: note.trim() ? "rgba(200,184,162,0.88)" : "rgba(200,184,162,0.1)", border: "none", borderRadius: "50px", color: note.trim() ? "#08080e" : "rgba(200,184,162,0.3)", fontSize: "15px", fontFamily: "Georgia, serif", fontWeight: "700", cursor: note.trim() ? "pointer" : "default", marginBottom: "12px", transition: "all 0.2s" }}>
              Save it
            </button>

            <button onClick={onSkip}
              style={{ width: "100%", padding: "14px", background: "none", border: "none", color: "rgba(200,184,162,0.3)", fontSize: "14px", fontFamily: "Georgia, serif", cursor: "pointer" }}>
              Skip for now
            </button>
          </>
        ) : (
          <div style={{ textAlign: "center", animation: "fadeUp 0.4s ease forwards" }}>
            <div style={{ fontSize: "28px", marginBottom: "16px" }}>💎</div>
            <div style={{ fontSize: "20px", color: "#c8b8a2", fontWeight: "400", marginBottom: "10px" }}>Saved.</div>
            <div style={{ fontSize: "14px", color: "rgba(200,184,162,0.4)", lineHeight: "1.7" }}>That's yours.</div>
          </div>
        )}
      </div>
    </div>
  );
}


// ── BREATHING SPACE ───────────────────────────────────────────────
const BREATHING_TRIGGERS = [
  "overwhelmed","can't cope","cannot cope","breaking point","panic","panicking",
  "anxious","anxiety","spiralling","spiraling","too much","falling apart",
  "exhausted","can't breathe","cannot breathe","losing it","losing my mind",
  "can't handle","cannot handle","about to break","at my limit","can't take",
  "cannot take","going crazy","losing control","heart racing","chest tight"
];

function detectBreathingMoment(text) {
  const lower = text.toLowerCase();
  return BREATHING_TRIGGERS.some(t => lower.includes(t));
}

function BreathingSpace({ onComplete }) {
  const [phase, setPhase] = useState("intro"); // intro | breathing | done
  const [breathPhase, setBreathPhase] = useState("in"); // in | hold1 | out | hold2
  const [count, setCount] = useState(4);
  const [cycle, setCycle] = useState(0);
  const [scale, setScale] = useState(1);
  const [visible, setVisible] = useState(false);
  const TOTAL_CYCLES = 4;

  useEffect(() => { setTimeout(() => setVisible(true), 100); }, []);

  useEffect(() => {
    if (phase !== "breathing") return;

    const phases = [
      { name: "in", label: "Breathe in", duration: 4, scale: 1.4 },
      { name: "hold1", label: "Hold", duration: 4, scale: 1.4 },
      { name: "out", label: "Breathe out", duration: 4, scale: 1 },
      { name: "hold2", label: "Hold", duration: 4, scale: 1 },
    ];

    const currentPhaseIndex = phases.findIndex(p => p.name === breathPhase);
    const current = phases[currentPhaseIndex];

    setScale(current.scale);

    if (count > 1) {
      const timer = setTimeout(() => setCount(c => c - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      const nextIndex = (currentPhaseIndex + 1) % phases.length;
      const timer = setTimeout(() => {
        if (nextIndex === 0) {
          const newCycle = cycle + 1;
          if (newCycle >= TOTAL_CYCLES) {
            setPhase("done");
            return;
          }
          setCycle(newCycle);
        }
        setBreathPhase(phases[nextIndex].name);
        setCount(phases[nextIndex].duration);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [phase, breathPhase, count, cycle]);

  const phaseLabels = { in: "Breathe in", hold1: "Hold", out: "Breathe out", hold2: "Hold" };
  const progress = ((cycle * 4 + ["in","hold1","out","hold2"].indexOf(breathPhase)) / (TOTAL_CYCLES * 4)) * 100;

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "#08080e", zIndex: 40, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 28px", maxWidth: "480px", margin: "0 auto", fontFamily: "Georgia, serif" }}>

      {/* Progress bar */}
      {phase === "breathing" && (
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "2px", background: "rgba(200,184,162,0.08)" }}>
          <div style={{ height: "100%", width: `${progress}%`, background: "rgba(200,184,162,0.4)", transition: "width 1s linear" }} />
        </div>
      )}

      <div style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(16px)", transition: "all 0.7s ease", textAlign: "center", width: "100%" }}>

        {phase === "intro" && (
          <>
            <div style={{ fontSize: "11px", letterSpacing: "0.4em", color: "rgba(200,184,162,0.3)", marginBottom: "32px" }}>SPILL</div>
            <div style={{ fontSize: "32px", marginBottom: "20px" }}>🌬️</div>
            <h2 style={{ fontSize: "24px", fontWeight: "400", color: "#e8e4dc", margin: "0 0 14px", lineHeight: "1.35" }}>
              Let's slow down<br />for a second.
            </h2>
            <p style={{ fontSize: "15px", color: "rgba(200,184,162,0.45)", lineHeight: "1.75", margin: "0 0 48px", maxWidth: "280px", marginLeft: "auto", marginRight: "auto" }}>
              Sixty seconds. It actually helps.
            </p>
            <button onClick={() => { setPhase("breathing"); setBreathPhase("in"); setCount(4); }}
              style={{ background: "rgba(200,184,162,0.88)", border: "none", borderRadius: "50px", padding: "17px 44px", color: "#08080e", fontSize: "15px", fontFamily: "Georgia, serif", fontWeight: "700", cursor: "pointer", marginBottom: "16px" }}>
              I'm ready
            </button>
            <br />
            <button onClick={onComplete}
              style={{ background: "none", border: "none", color: "rgba(200,184,162,0.3)", fontSize: "14px", fontFamily: "Georgia, serif", cursor: "pointer", padding: "12px" }}>
              Skip for now
            </button>
          </>
        )}

        {phase === "breathing" && (
          <>
            <div style={{ fontSize: "11px", letterSpacing: "0.4em", color: "rgba(200,184,162,0.3)", marginBottom: "48px" }}>SPILL</div>

            {/* Breathing circle */}
            <div style={{ position: "relative", width: "180px", height: "180px", margin: "0 auto 40px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{
                width: "180px", height: "180px", borderRadius: "50%",
                background: "radial-gradient(circle, rgba(200,184,162,0.12) 0%, rgba(200,184,162,0.04) 70%)",
                border: "1px solid rgba(200,184,162,0.2)",
                transform: `scale(${scale})`,
                transition: "transform 4s ease-in-out",
                position: "absolute"
              }} />
              <div style={{ position: "relative", zIndex: 1, textAlign: "center" }}>
                <div style={{ fontSize: "42px", fontWeight: "300", color: "#c8b8a2", lineHeight: 1 }}>{count}</div>
              </div>
            </div>

            <div style={{ fontSize: "20px", fontWeight: "400", color: "#e8e4dc", marginBottom: "12px" }}>
              {phaseLabels[breathPhase]}
            </div>
            <div style={{ fontSize: "13px", color: "rgba(200,184,162,0.35)" }}>
              Cycle {cycle + 1} of {TOTAL_CYCLES}
            </div>

            <button onClick={onComplete}
              style={{ position: "absolute", bottom: "32px", background: "none", border: "none", color: "rgba(200,184,162,0.2)", fontSize: "13px", fontFamily: "Georgia, serif", cursor: "pointer" }}>
              Skip
            </button>
          </>
        )}

        {phase === "done" && (
          <>
            <div style={{ fontSize: "32px", marginBottom: "20px" }}>✓</div>
            <h2 style={{ fontSize: "24px", fontWeight: "400", color: "#e8e4dc", margin: "0 0 14px" }}>
              Good.
            </h2>
            <p style={{ fontSize: "15px", color: "rgba(200,184,162,0.45)", lineHeight: "1.75", margin: "0 0 48px" }}>
              Take your time.
            </p>
            <button onClick={onComplete}
              style={{ background: "rgba(200,184,162,0.88)", border: "none", borderRadius: "50px", padding: "17px 44px", color: "#08080e", fontSize: "15px", fontFamily: "Georgia, serif", fontWeight: "700", cursor: "pointer" }}>
              Continue
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function FocusMode({ onDismiss }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    setTimeout(() => setVisible(true), 100);
    // Auto dismiss after 4 seconds
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(onDismiss, 400);
    }, 4000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(8,8,14,0.92)", zIndex: 30,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: "40px", textAlign: "center",
      opacity: visible ? 1 : 0,
      transition: "opacity 0.4s ease",
      maxWidth: "480px", margin: "0 auto",
    }}>
      <div style={{
        width: "64px", height: "64px", borderRadius: "50%",
        border: "1px solid rgba(200,184,162,0.2)",
        display: "flex", alignItems: "center", justifyContent: "center",
        marginBottom: "28px", background: "rgba(200,184,162,0.04)",
        fontSize: "26px"
      }}>🤫</div>
      <div style={{
        fontSize: "22px", fontWeight: "400", color: "#e8e4dc",
        fontFamily: "Georgia, serif", marginBottom: "14px", lineHeight: "1.3"
      }}>
        This space is yours.
      </div>
      <div style={{
        fontSize: "15px", color: "rgba(200,184,162,0.5)",
        fontFamily: "Georgia, serif", lineHeight: "1.7", marginBottom: "36px",
        maxWidth: "260px"
      }}>
        Notifications can wait.<br />
        This time is yours.
      </div>
      <button onClick={() => { setVisible(false); setTimeout(onDismiss, 400); }}
        style={{
          background: "none", border: "1px solid rgba(200,184,162,0.2)",
          borderRadius: "20px", padding: "10px 24px",
          color: "rgba(200,184,162,0.45)", fontSize: "13px",
          fontFamily: "Georgia, serif", cursor: "pointer"
        }}>
        Got it
      </button>
    </div>
  );
}

function BrotherhoodScreen({ nuggets, onSelectCircle, onClose }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 100); }, []);
  const allNuggets = [...SEED_NUGGETS, ...nuggets];

  return (
    <div style={{ minHeight: "100vh", background: "#08080e", display: "flex", flexDirection: "column", fontFamily: "Georgia, serif" }}>
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "radial-gradient(ellipse at 50% 0%, rgba(40,30,60,0.3) 0%, transparent 60%)", pointerEvents: "none", zIndex: 0 }} />
      <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "rgba(8,8,14,0.96)", backdropFilter: "blur(12px)", borderBottom: "1px solid rgba(255,255,255,0.05)", zIndex: 10 }}>
        <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(200,184,162,0.4)", cursor: "pointer", fontSize: "22px", lineHeight: 1 }}>‹</button>
        <div style={{ fontSize: "11px", letterSpacing: "0.4em", color: "rgba(200,184,162,0.35)" }}>SPILL</div>
        <div style={{ width: "22px" }} />
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "32px 24px 40px", zIndex: 1, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(16px)", transition: "all 0.8s ease" }}>
        <div style={{ marginBottom: "40px" }}>
          <h1 style={{ fontSize: "30px", fontWeight: "400", color: "#e8e4dc", margin: "0 0 10px" }}>Brotherhood</h1>
          <p style={{ fontSize: "15px", color: "rgba(200,184,162,0.45)", margin: 0, lineHeight: "1.6" }}>You're not the only one.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {CIRCLES.map((circle, i) => {
            const count = allNuggets.filter(n => n.category && n.category.toLowerCase().trim() === circle.key.toLowerCase().trim()).length;
            return (
              <button key={circle.key} onClick={() => onSelectCircle(circle)}
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(200,184,162,0.08)", borderRadius: "16px", padding: "20px", textAlign: "left", cursor: "pointer", color: "#e8e4dc", fontFamily: "Georgia, serif", transition: "all 0.2s ease", opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(16px)", transitionDelay: `${0.1 + i * 0.08}s` }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(200,184,162,0.06)"; e.currentTarget.style.borderColor = "rgba(200,184,162,0.18)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.03)"; e.currentTarget.style.borderColor = "rgba(200,184,162,0.08)"; }}>
                <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                  <div style={{ fontSize: "24px", width: "46px", height: "46px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(200,184,162,0.06)", borderRadius: "12px", flexShrink: 0 }}>{circle.emoji}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "16px", fontWeight: "600", color: "#c8b8a2", marginBottom: "4px" }}>{circle.label}</div>
                    <div style={{ fontSize: "13px", color: "rgba(200,184,162,0.4)" }}>{circle.desc}</div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: "16px", color: "rgba(200,184,162,0.5)", fontWeight: "600" }}>{count}</div>
                    <div style={{ fontSize: "10px", color: "rgba(200,184,162,0.25)", letterSpacing: "0.05em" }}>nuggets</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        <div style={{ marginTop: "40px", padding: "20px", background: "rgba(200,184,162,0.03)", border: "1px solid rgba(200,184,162,0.07)", borderRadius: "14px" }}>
          <div style={{ fontSize: "13px", color: "rgba(200,184,162,0.35)", lineHeight: "1.7", fontStyle: "italic", textAlign: "center" }}>
            Circles grow when people leave something real behind.
          </div>
        </div>
      </div>
      <CrisisLine />
    </div>
  );
}

function CircleFeedScreen({ circle, nuggets, onClose }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 100); }, []);

  // FIX: ensure category key comparison is lowercase and trimmed
  const allNuggets = [...SEED_NUGGETS, ...nuggets];
  const circleNuggets = allNuggets
    .filter(n => n.category && n.category.toLowerCase().trim() === circle.key.toLowerCase().trim())
    .sort((a, b) => b.timestamp - a.timestamp);

  return (
    <div style={{ minHeight: "100vh", background: "#08080e", display: "flex", flexDirection: "column", fontFamily: "Georgia, serif" }}>
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "radial-gradient(ellipse at 20% 80%, rgba(30,40,80,0.2) 0%, transparent 60%)", pointerEvents: "none", zIndex: 0 }} />
      <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "rgba(8,8,14,0.96)", backdropFilter: "blur(12px)", borderBottom: "1px solid rgba(255,255,255,0.05)", zIndex: 10 }}>
        <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(200,184,162,0.4)", cursor: "pointer", fontSize: "22px", lineHeight: 1 }}>‹</button>
        <div style={{ fontSize: "11px", letterSpacing: "0.4em", color: "rgba(200,184,162,0.35)" }}>SPILL</div>
        <div style={{ width: "22px" }} />
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "28px 20px 40px", zIndex: 1, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(12px)", transition: "all 0.7s ease" }}>
        <div style={{ marginBottom: "32px" }}>
          <div style={{ fontSize: "28px", marginBottom: "10px" }}>{circle.emoji}</div>
          <h2 style={{ fontSize: "24px", fontWeight: "400", color: "#e8e4dc", margin: "0 0 8px" }}>{circle.label}</h2>
          <p style={{ fontSize: "14px", color: "rgba(200,184,162,0.4)", margin: 0, lineHeight: "1.6" }}>Real words. No filter.</p>
        </div>
        {circleNuggets.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 20px" }}>
            <div style={{ fontSize: "32px", marginBottom: "16px" }}>💎</div>
            <div style={{ fontSize: "14px", color: "rgba(200,184,162,0.35)", lineHeight: "1.8" }}>Nothing here yet.<br />Could be you that starts it.</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {circleNuggets.map((n, i) => (
              <div key={i} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(200,184,162,0.08)", borderRadius: "16px", padding: "20px" }}>
                <div style={{ fontSize: "15px", color: "#c8b8a2", lineHeight: "1.7", fontStyle: "italic", marginBottom: "16px" }}>"{n.text}"</div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ fontSize: "12px", color: "rgba(200,184,162,0.25)" }}>— Anonymous</div>
                  <div style={{ display: "flex", gap: "14px" }}>
                    <span style={{ fontSize: "12px", color: "rgba(200,184,162,0.35)" }}>🤝 {n.strength || 0}</span>
                    <span style={{ fontSize: "12px", color: "rgba(200,184,162,0.35)" }}>💪 {n.respect || 0}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop: "36px", padding: "18px 20px", background: "rgba(200,184,162,0.02)", border: "1px dashed rgba(200,184,162,0.1)", borderRadius: "14px", textAlign: "center" }}>
          <div style={{ fontSize: "13px", color: "rgba(200,184,162,0.3)", lineHeight: "1.7", fontStyle: "italic" }}>Something worth leaving behind?<br />It starts with saying it out loud.</div>
        </div>
      </div>
      <CrisisLine />
    </div>
  );
}

function ProfileScreen({ profile, nuggets, conversationCount, onUpdateHandle, onClose, onOpenBrotherhood }) {
  const [editingHandle, setEditingHandle] = useState(false);
  const [handleInput, setHandleInput] = useState(profile.handle || "");
  const [showNuggets, setShowNuggets] = useState(false);
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 100); }, []);

  const joinDate = profile.joinDate ? new Date(profile.joinDate).toLocaleDateString("en-GB", { month: "long", year: "numeric" }) : "Today";
  const daysActive = profile.joinDate ? Math.max(1, Math.floor((Date.now() - profile.joinDate) / 86400000)) : 1;

  if (showNuggets) return <NuggetsLibrary nuggets={nuggets} onClose={() => setShowNuggets(false)} isProfile />;

  return (
    <div style={{ minHeight: "100vh", background: "#08080e", display: "flex", flexDirection: "column", fontFamily: "Georgia, serif", color: "#e8e4dc" }}>
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "radial-gradient(ellipse at 80% 20%, rgba(60,30,60,0.2) 0%, transparent 60%)", pointerEvents: "none", zIndex: 0 }} />
      <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "rgba(8,8,14,0.96)", backdropFilter: "blur(12px)", borderBottom: "1px solid rgba(255,255,255,0.05)", zIndex: 10 }}>
        <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(200,184,162,0.4)", cursor: "pointer", fontSize: "22px", lineHeight: 1 }}>‹</button>
        <div style={{ fontSize: "11px", letterSpacing: "0.4em", color: "rgba(200,184,162,0.35)" }}>SPILL</div>
        <div style={{ width: "22px" }} />
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "32px 24px 40px", zIndex: 1, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(16px)", transition: "all 0.7s ease" }}>
        <div style={{ marginBottom: "40px" }}>
          <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: "rgba(200,184,162,0.08)", border: "1px solid rgba(200,184,162,0.15)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px", fontSize: "26px" }}>🧍</div>
          {editingHandle ? (
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <input value={handleInput} onChange={e => setHandleInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && (() => { onUpdateHandle(handleInput.trim() || "Anonymous"); setEditingHandle(false); })()}
                autoFocus placeholder="Enter a name or handle..."
                style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(200,184,162,0.2)", borderRadius: "10px", padding: "10px 14px", color: "#e8e4dc", fontSize: "16px", fontFamily: "Georgia, serif", outline: "none", flex: 1 }} />
              <button onClick={() => { onUpdateHandle(handleInput.trim() || "Anonymous"); setEditingHandle(false); }}
                style={{ background: "rgba(200,184,162,0.88)", border: "none", borderRadius: "10px", padding: "10px 16px", color: "#08080e", fontFamily: "Georgia, serif", fontWeight: "700", cursor: "pointer", fontSize: "13px" }}>Save</button>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{ fontSize: "22px", fontWeight: "400", color: "#e8e4dc" }}>{profile.handle || "Anonymous"}</div>
              <button onClick={() => setEditingHandle(true)} style={{ background: "none", border: "none", color: "rgba(200,184,162,0.3)", cursor: "pointer", fontSize: "13px", fontFamily: "Georgia, serif", padding: 0 }}>edit</button>
            </div>
          )}
          <div style={{ fontSize: "12px", color: "rgba(200,184,162,0.25)", marginTop: "4px" }}>Member since {joinDate} · Private</div>
        </div>
        <div style={{ marginBottom: "40px" }}>
          <div style={{ fontSize: "11px", letterSpacing: "0.3em", color: "rgba(200,184,162,0.3)", marginBottom: "16px" }}>YOUR JOURNEY</div>
          <div style={{ display: "flex", gap: "12px" }}>
            <div style={{ flex: 1, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(200,184,162,0.08)", borderRadius: "14px", padding: "18px 16px" }}>
              <div style={{ fontSize: "24px", color: "#c8b8a2", fontWeight: "600", marginBottom: "4px" }}>{daysActive}</div>
              <div style={{ fontSize: "12px", color: "rgba(200,184,162,0.4)", lineHeight: "1.4" }}>{daysActive === 1 ? "Day" : "Days"} showing up</div>
            </div>
            <div style={{ flex: 1, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(200,184,162,0.08)", borderRadius: "14px", padding: "18px 16px" }}>
              <div style={{ fontSize: "24px", color: "#c8b8a2", fontWeight: "600", marginBottom: "4px" }}>{conversationCount}</div>
              <div style={{ fontSize: "12px", color: "rgba(200,184,162,0.4)", lineHeight: "1.4" }}>Honest {conversationCount === 1 ? "chat" : "chats"}</div>
            </div>
            <div style={{ flex: 1, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(200,184,162,0.08)", borderRadius: "14px", padding: "18px 16px" }}>
              <div style={{ fontSize: "24px", color: "#c8b8a2", fontWeight: "600", marginBottom: "4px" }}>{nuggets.length}</div>
              <div style={{ fontSize: "12px", color: "rgba(200,184,162,0.4)", lineHeight: "1.4" }}>Things saved</div>
            </div>
          </div>
        </div>
        <div style={{ marginBottom: "40px" }}>
          <div style={{ fontSize: "11px", letterSpacing: "0.3em", color: "rgba(200,184,162,0.3)", marginBottom: "16px" }}>YOUR NUGGETS</div>
          {nuggets.length === 0 ? (
            <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(200,184,162,0.07)", borderRadius: "14px", padding: "24px 20px", textAlign: "center" }}>
              <div style={{ fontSize: "28px", marginBottom: "10px" }}>💎</div>
              <div style={{ fontSize: "14px", color: "rgba(200,184,162,0.4)", lineHeight: "1.7" }}>Nothing here yet.<br />Something will land eventually.</div>
            </div>
          ) : (
            <div>
              {nuggets.slice(0, 2).map((n, i) => (
                <div key={i} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(200,184,162,0.08)", borderRadius: "14px", padding: "16px 18px", marginBottom: "10px" }}>
                  <div style={{ fontSize: "14px", color: "#c8b8a2", fontStyle: "italic", lineHeight: "1.6", marginBottom: "8px" }}>"{n.text.length > 100 ? n.text.slice(0, 100) + "…" : n.text}"</div>
                  <div style={{ fontSize: "11px", color: "rgba(200,184,162,0.3)" }}>{NUGGET_CATEGORIES.find(c => c.key === n.category)?.emoji} {NUGGET_CATEGORIES.find(c => c.key === n.category)?.label}</div>
                </div>
              ))}
              <button onClick={() => setShowNuggets(true)} style={{ width: "100%", padding: "14px", background: "rgba(200,184,162,0.06)", border: "1px solid rgba(200,184,162,0.1)", borderRadius: "14px", color: "rgba(200,184,162,0.6)", fontFamily: "Georgia, serif", fontSize: "13px", cursor: "pointer" }}>
                View all {nuggets.length} nugget{nuggets.length !== 1 ? "s" : ""} →
              </button>
            </div>
          )}
        </div>
        {/* Data rights */}
        <div style={{ marginBottom: "40px" }}>
          <div style={{ fontSize: "11px", letterSpacing: "0.3em", color: "rgba(200,184,162,0.3)", marginBottom: "16px" }}>YOUR DATA</div>
          <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(200,184,162,0.07)", borderRadius: "14px", padding: "18px 20px" }}>
            <div style={{ fontSize: "13px", color: "rgba(200,184,162,0.4)", lineHeight: "1.7", marginBottom: "12px" }}>
              Your data is stored anonymously. No name or email is linked to your account.
            </div>
            <a href="mailto:privacy@spillit.co.uk?subject=Data Deletion Request" style={{ fontSize: "13px", color: "rgba(200,184,162,0.5)", fontFamily: "Georgia, serif", textDecoration: "none", borderBottom: "1px solid rgba(200,184,162,0.2)", paddingBottom: "1px" }}>
              Request data deletion →
            </a>
          </div>
        </div>

        <div>
          <div style={{ fontSize: "11px", letterSpacing: "0.3em", color: "rgba(200,184,162,0.3)", marginBottom: "16px" }}>YOUR CIRCLES</div>
          <button onClick={onOpenBrotherhood} style={{ width: "100%", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(200,184,162,0.1)", borderRadius: "14px", padding: "20px", textAlign: "left", cursor: "pointer", fontFamily: "Georgia, serif" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: "15px", color: "#c8b8a2", marginBottom: "4px" }}>🤝 Brotherhood Circles</div>
                <div style={{ fontSize: "13px", color: "rgba(200,184,162,0.4)" }}>5 circles · {[...SEED_NUGGETS, ...nuggets].length} nuggets</div>
              </div>
              <div style={{ color: "rgba(200,184,162,0.3)", fontSize: "20px" }}>›</div>
            </div>
          </button>
        </div>
      </div>
      <CrisisLine />
    </div>
  );
}

function NuggetsLibrary({ nuggets, onClose, isProfile }) {
  const [filter, setFilter] = useState("all");
  const filtered = filter === "all" ? nuggets : nuggets.filter(n => n.category && n.category.toLowerCase().trim() === filter.toLowerCase().trim());
  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "#08080e", zIndex: 100, display: "flex", flexDirection: "column", maxWidth: "480px", margin: "0 auto" }}>
      <div style={{ padding: "20px 20px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        <div>
          <div style={{ fontSize: "11px", letterSpacing: "0.4em", color: "rgba(200,184,162,0.35)", fontFamily: "Georgia, serif" }}>SPILL</div>
          <div style={{ fontSize: "20px", fontWeight: "400", color: "#e8e4dc", fontFamily: "Georgia, serif", marginTop: "4px" }}>💎 {isProfile ? "Your Nuggets" : "Gems & Nuggets"}</div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(200,184,162,0.4)", cursor: "pointer", fontSize: "24px" }}>×</button>
      </div>
      <div style={{ padding: "8px 20px 16px", display: "flex", gap: "8px", overflowX: "auto" }}>
        {[{ key: "all", label: "All", emoji: "✨" }, ...NUGGET_CATEGORIES].map(cat => (
          <button key={cat.key} onClick={() => setFilter(cat.key)}
            style={{ background: filter === cat.key ? "rgba(200,184,162,0.15)" : "rgba(255,255,255,0.03)", border: `1px solid ${filter === cat.key ? "rgba(200,184,162,0.3)" : "rgba(200,184,162,0.08)"}`, borderRadius: "20px", padding: "6px 14px", color: filter === cat.key ? "#c8b8a2" : "rgba(200,184,162,0.4)", fontSize: "12px", cursor: "pointer", whiteSpace: "nowrap", fontFamily: "Georgia, serif" }}>
            {cat.emoji} {cat.label}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "0 20px 24px", display: "flex", flexDirection: "column", gap: "12px" }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", color: "rgba(200,184,162,0.3)", fontFamily: "Georgia, serif", fontSize: "14px", marginTop: "40px", lineHeight: "1.8" }}>No nuggets here yet.</div>
        ) : filtered.map((n, i) => (
          <div key={i} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(200,184,162,0.1)", borderRadius: "14px", padding: "18px" }}>
            <div style={{ fontSize: "15px", color: "#c8b8a2", fontFamily: "Georgia, serif", lineHeight: "1.65", fontStyle: "italic", marginBottom: "12px" }}>"{n.text}"</div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: "11px", color: "rgba(200,184,162,0.3)", fontFamily: "Georgia, serif" }}>{NUGGET_CATEGORIES.find(c => c.key === n.category)?.emoji} {NUGGET_CATEGORIES.find(c => c.key === n.category)?.label}</span>
              <div style={{ display: "flex", gap: "12px" }}>
                <span style={{ fontSize: "12px", color: "rgba(200,184,162,0.4)", fontFamily: "Georgia, serif" }}>🤝 {n.strength || 0}</span>
                <span style={{ fontSize: "12px", color: "rgba(200,184,162,0.4)", fontFamily: "Georgia, serif" }}>💪 {n.respect || 0}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SaveNuggetModal({ text, onSave, onDismiss }) {
  const suggested = suggestCategory(text);
  const [selected, setSelected] = useState(suggested);
  const [editedText, setEditedText] = useState(text);
  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(8,8,14,0.92)", zIndex: 50, display: "flex", flexDirection: "column", justifyContent: "flex-end", maxWidth: "480px", margin: "0 auto" }}>
      <div style={{ background: "#0f0f18", borderTop: "1px solid rgba(200,184,162,0.1)", borderRadius: "20px 20px 0 0", padding: "28px 24px 40px" }}>
        <div style={{ fontSize: "20px", color: "#e8e4dc", fontFamily: "Georgia, serif", marginBottom: "6px" }}>💎 That sounded like something worth keeping.</div>
        <p style={{ fontSize: "13px", color: "rgba(200,184,162,0.45)", fontFamily: "Georgia, serif", lineHeight: "1.6", margin: "0 0 20px" }}>Want to leave that for someone else who might need it?</p>
        <textarea value={editedText} onChange={e => setEditedText(e.target.value)}
          style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(200,184,162,0.15)", borderRadius: "12px", padding: "14px", color: "#c8b8a2", fontSize: "14px", fontFamily: "Georgia, serif", resize: "none", outline: "none", lineHeight: "1.6", marginBottom: "16px", boxSizing: "border-box", fontStyle: "italic" }}
          rows={3} />
        {suggested && (
          <div style={{ fontSize: "12px", color: "rgba(200,184,162,0.4)", fontFamily: "Georgia, serif", marginBottom: "8px" }}>
            ✨ Suggested: <span style={{ color: "#c8b8a2" }}>{NUGGET_CATEGORIES.find(c => c.key === suggested)?.emoji} {NUGGET_CATEGORIES.find(c => c.key === suggested)?.label}</span>
          </div>
        )}
        <div style={{ fontSize: "12px", color: "rgba(200,184,162,0.4)", fontFamily: "Georgia, serif", marginBottom: "12px", letterSpacing: "0.05em" }}>CHOOSE A CATEGORY</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "24px" }}>
          {NUGGET_CATEGORIES.map(cat => (
            <button key={cat.key} onClick={() => setSelected(cat.key)}
              style={{ background: selected === cat.key ? "rgba(200,184,162,0.15)" : "rgba(255,255,255,0.03)", border: `1px solid ${selected === cat.key ? "rgba(200,184,162,0.35)" : "rgba(200,184,162,0.08)"}`, borderRadius: "20px", padding: "7px 14px", color: selected === cat.key ? "#c8b8a2" : "rgba(200,184,162,0.4)", fontSize: "12px", cursor: "pointer", fontFamily: "Georgia, serif" }}>
              {cat.emoji} {cat.label}
            </button>
          ))}
        </div>
        <button onClick={() => selected && onSave(editedText, selected)} disabled={!selected}
          style={{ width: "100%", padding: "16px", background: selected ? "rgba(200,184,162,0.88)" : "rgba(200,184,162,0.1)", border: "none", borderRadius: "50px", color: selected ? "#08080e" : "rgba(200,184,162,0.3)", fontSize: "15px", fontFamily: "Georgia, serif", fontWeight: "700", cursor: selected ? "pointer" : "default", marginBottom: "12px", transition: "all 0.2s" }}>
          Leave it anonymously
        </button>
        <button onClick={onDismiss} style={{ width: "100%", padding: "14px", background: "none", border: "none", color: "rgba(200,184,162,0.35)", fontSize: "14px", fontFamily: "Georgia, serif", cursor: "pointer" }}>Not this time</button>
      </div>
    </div>
  );
}


// ── CHECK-IN SCREEN ───────────────────────────────────────────────
function CheckInScreen({ onComplete }) {
  const [selected, setSelected] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 150); }, []);

  const options = [
    { key: "heavy", label: "Heavy", color: "rgba(180,100,100,0.15)", border: "rgba(180,100,100,0.3)", opening: "What's heaviest right now?" },
    { key: "okay", label: "Okay", color: "rgba(200,184,162,0.08)", border: "rgba(200,184,162,0.2)", opening: "What's on your mind today?" },
    { key: "good", label: "Good", color: "rgba(100,180,120,0.12)", border: "rgba(100,180,120,0.25)", opening: "Good to hear. What's been going well?" },
  ];

  const handleSelect = (option) => {
    setSelected(option.key);
    setTransitioning(true);
    // 1 second acknowledgement pause — "Got you."
    setTimeout(() => {
      onComplete(option);
    }, 1800);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#08080e", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 28px", position: "relative", overflow: "hidden", fontFamily: "Georgia, serif" }}>
      <div style={{ position: "absolute", top: "30%", left: "50%", transform: "translateX(-50%)", width: "280px", height: "280px", background: "radial-gradient(circle, rgba(200,184,162,0.05) 0%, transparent 70%)", pointerEvents: "none" }} />

      <div style={{ width: "100%", maxWidth: "320px", zIndex: 1, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: "all 0.9s ease" }}>

        {!transitioning ? (
          <>
            <div style={{ fontSize: "11px", letterSpacing: "0.4em", color: "rgba(200,184,162,0.3)", marginBottom: "32px", textAlign: "center" }}>SPILL</div>
            <h2 style={{ fontSize: "26px", fontWeight: "400", color: "#e8e4dc", margin: "0 0 12px", lineHeight: "1.3", textAlign: "center" }}>
              How are you carrying<br />things today?
            </h2>
            <p style={{ fontSize: "14px", color: "rgba(200,184,162,0.35)", textAlign: "center", margin: "0 0 48px", lineHeight: "1.6" }}>
              Good to have you back.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {options.map((option) => (
                <button key={option.key} onClick={() => handleSelect(option)}
                  style={{
                    background: selected === option.key ? option.color : "rgba(255,255,255,0.03)",
                    border: `1px solid ${selected === option.key ? option.border : "rgba(200,184,162,0.1)"}`,
                    borderRadius: "14px", padding: "18px 24px",
                    color: selected === option.key ? "#e8e4dc" : "#c8b8a2",
                    fontSize: "17px", fontFamily: "Georgia, serif",
                    cursor: "pointer", transition: "all 0.2s ease",
                    textAlign: "center", letterSpacing: "0.03em",
                  }}
                  onMouseEnter={e => { if (!selected) { e.currentTarget.style.background = option.color; e.currentTarget.style.borderColor = option.border; }}}
                  onMouseLeave={e => { if (!selected) { e.currentTarget.style.background = "rgba(255,255,255,0.03)"; e.currentTarget.style.borderColor = "rgba(200,184,162,0.1)"; }}}>
                  {option.label}
                </button>
              ))}
            </div>
          </>
        ) : (
          <div style={{ textAlign: "center", animation: "fadeUp 0.4s ease forwards" }}>
            <div style={{ fontSize: "28px", color: "#c8b8a2", fontWeight: "400", marginBottom: "12px" }}>Got you.</div>
            <div style={{ fontSize: "14px", color: "rgba(200,184,162,0.4)", lineHeight: "1.6" }}>One second...</div>
          </div>
        )}
      </div>

      <div style={{ position: "absolute", bottom: "24px", fontSize: "11px", color: "rgba(200,184,162,0.2)", textAlign: "center" }}>
        In crisis? Samaritans: <span style={{ color: "rgba(200,184,162,0.4)" }}>116 123</span>
      </div>

      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
      `}</style>
    </div>
  );
}


// ── DISCLAIMER & GENDER GATE ──────────────────────────────────────

// ── LOADING SCREEN ────────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div style={{ minHeight: "100vh", background: "#08080e", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "Georgia, serif" }}>
      <div style={{ marginBottom: "24px" }}>
        <svg width="48" height="48" viewBox="0 0 64 64" fill="none">
          <path d="M18 14 L46 14 L42 44 L22 44 Z" fill="rgba(200,184,162,0.08)" stroke="rgba(200,184,162,0.5)" strokeWidth="1.5" transform="rotate(-20, 32, 29)"/>
          <path d="M38 34 C42 38 44 46 40 54 C37 58 33 58 31 56" stroke="rgba(200,184,162,0.7)" strokeWidth="2" strokeLinecap="round" fill="none"/>
          <circle cx="43" cy="50" r="2.5" fill="rgba(200,184,162,0.4)"/>
          <circle cx="36" cy="56" r="1.5" fill="rgba(200,184,162,0.3)"/>
        </svg>
      </div>
      <div style={{ fontSize: "11px", letterSpacing: "0.45em", color: "rgba(200,184,162,0.3)" }}>SPILL</div>
      <div style={{ marginTop: "32px", display: "flex", gap: "6px" }}>
        {[0,1,2].map(i => <div key={i} style={{ width: "6px", height: "6px", borderRadius: "50%", background: "rgba(200,184,162,0.3)", animation: `pulse 1.2s ease-in-out ${i*0.2}s infinite` }} />)}
      </div>
      <style>{`@keyframes pulse { 0%,100% { opacity:0.3; transform:scale(0.8); } 50% { opacity:1; transform:scale(1.2); } }`}</style>
    </div>
  );
}

function DisclaimerScreen({ onAccept }) {
  const [step, setStep] = useState("gender"); // gender → disclaimer → accepted
  const [visible, setVisible] = useState(false);
  const [scrolled, setScrolled] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => { setTimeout(() => setVisible(true), 150); }, []);

  const handleScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop <= el.clientHeight + 40) {
      setScrolled(true);
    }
  };

  if (step === "gender") {
    return (
      <div style={{ minHeight: "100vh", background: "#08080e", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 28px", fontFamily: "Georgia, serif", position: "relative" }}>
        <div style={{ position: "absolute", top: "25%", left: "50%", transform: "translateX(-50%)", width: "280px", height: "280px", background: "radial-gradient(circle, rgba(200,184,162,0.05) 0%, transparent 70%)", pointerEvents: "none" }} />

        <div style={{ maxWidth: "320px", width: "100%", textAlign: "center", zIndex: 1, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: "all 0.9s ease" }}>
          <div style={{ margin: "0 auto 28px", display: "flex", justifyContent: "center" }}>
            <svg width="56" height="56" viewBox="0 0 64 64" fill="none">
              <path d="M18 14 L46 14 L42 44 L22 44 Z" fill="rgba(200,184,162,0.08)" stroke="rgba(200,184,162,0.5)" strokeWidth="1.5" transform="rotate(-20, 32, 29)"/>
              <path d="M38 34 C42 38 44 46 40 54 C37 58 33 58 31 56" stroke="rgba(200,184,162,0.7)" strokeWidth="2" strokeLinecap="round" fill="none"/>
              <circle cx="43" cy="50" r="2.5" fill="rgba(200,184,162,0.4)"/>
              <circle cx="36" cy="56" r="1.5" fill="rgba(200,184,162,0.3)"/>
            </svg>
          </div>
          <div style={{ fontSize: "11px", letterSpacing: "0.4em", color: "rgba(200,184,162,0.3)", marginBottom: "24px" }}>SPILL</div>
          <h2 style={{ fontSize: "26px", fontWeight: "400", color: "#e8e4dc", margin: "0 0 16px", lineHeight: "1.35" }}>
            Spill was built with men in mind.
          </h2>
          <p style={{ fontSize: "15px", color: "rgba(200,184,162,0.5)", lineHeight: "1.75", margin: "0 0 48px" }}>
            A private space built around how men actually open up. Not how people assume they do.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "32px" }}>
            <button onClick={() => { setVisible(false); setTimeout(() => { setStep("disclaimer"); setVisible(false); setTimeout(() => setVisible(true), 100); }, 300); }}
              style={{ background: "rgba(200,184,162,0.88)", border: "none", borderRadius: "14px", padding: "18px 24px", color: "#08080e", fontSize: "16px", fontFamily: "Georgia, serif", fontWeight: "700", cursor: "pointer", transition: "all 0.2s" }}>
              Yeah, that's me
            </button>
            <button onClick={() => window.open("https://www.samaritans.org", "_blank")}
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(200,184,162,0.1)", borderRadius: "14px", padding: "18px 24px", color: "rgba(200,184,162,0.5)", fontSize: "15px", fontFamily: "Georgia, serif", cursor: "pointer", transition: "all 0.2s" }}>
              I need something else
            </button>
          </div>

          <p style={{ fontSize: "12px", color: "rgba(200,184,162,0.2)", lineHeight: "1.6" }}>
            If you're in crisis right now, please call<br />
            <span style={{ color: "rgba(200,184,162,0.45)" }}>Samaritans free on 116 123</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#08080e", display: "flex", flexDirection: "column", fontFamily: "Georgia, serif" }}>
      {/* Header */}
      <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(8,8,14,0.98)", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        <button onClick={() => setStep("gender")} style={{ background: "none", border: "none", color: "rgba(200,184,162,0.4)", cursor: "pointer", fontSize: "22px", lineHeight: 1 }}>‹</button>
        <div style={{ fontSize: "11px", letterSpacing: "0.4em", color: "rgba(200,184,162,0.35)" }}>SPILL</div>
        <div style={{ width: "22px" }} />
      </div>

      {/* Title */}
      <div style={{ padding: "28px 24px 16px" }}>
        <h2 style={{ fontSize: "22px", fontWeight: "400", color: "#e8e4dc", margin: "0 0 8px" }}>Before you go in</h2>
        <p style={{ fontSize: "14px", color: "rgba(200,184,162,0.4)", margin: 0, lineHeight: "1.6" }}>Quick read. Worth knowing.</p>
      </div>

      {/* Scrollable terms */}
      <div ref={scrollRef} onScroll={handleScroll} style={{ flex: 1, overflowY: "auto", padding: "0 24px 24px" }}>
        <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(200,184,162,0.08)", borderRadius: "16px", padding: "24px", marginBottom: "20px" }}>

          {[
            {
              title: "What this is",
              text: "Spill is a private AI-powered conversation tool designed to support men's mental wellbeing. It is not a medical service, therapy service, or crisis intervention service. Spill does not provide professional mental health care, diagnosis, or treatment."
            },
            {
              title: "What this isn't",
              text: "Spill is not a licensed therapist, counsellor, psychologist, or medical professional. Conversations with Spill do not constitute medical or psychological advice. Spill cannot diagnose mental health conditions or prescribe treatment of any kind."
            },
            {
              title: "If things get serious",
              text: "If you are in immediate danger, experiencing suicidal thoughts, or in a mental health crisis, stop using Spill immediately and contact emergency services on 999, or Samaritans free on 116 123 (available 24 hours, 7 days a week). Spill is not a crisis service and must never be used as a substitute for emergency help. While Spill may suggest professional support during difficult conversations, it cannot reliably detect all crisis situations and you should never depend on it to do so."
            },
            {
              title: "Your call",
              text: "By using Spill, you acknowledge that you are responsible for your own decisions and actions. Spill provides conversational support only. Nothing said by Spill should be interpreted as professional advice or an instruction to take any specific action."
            },
            {
              title: "What we can't promise",
              text: "Spill and its creator accept no liability for any harm, loss, or damage arising from the use of this application. Spill is a support tool and should never be used as a substitute for professional medical, psychological, or emergency services."
            },
            {
              title: "Privacy",
              text: "Conversations with Spill are private and not shared with third parties. No name, email, or personally identifiable information is required to use Spill. Spill uses an anonymous ID stored on your device to save your profile, nuggets, check-ins, and exit notes in a secure database. Conversation content itself is not stored. You can request deletion of all your data at any time by contacting us at privacy@spillit.co.uk."
            },
            {
              title: "How to use it",
              text: "Spill is designed for men seeking a private space for reflection and support. It must not be used to encourage, plan, or facilitate harm to yourself or others. Any misuse of Spill for harmful purposes is strictly prohibited."
            },
            {
              title: "Your data",
              text: "Under UK GDPR you have the right to access, correct, or delete your personal data at any time. To request deletion of all data associated with your anonymous ID, contact privacy@spillit.co.uk. All data will be permanently deleted within 30 days of your request. You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at ico.org.uk."
            },
            {
              title: "Who it's for",
              text: "Spill is intended for users aged 18 and over. By accepting these terms you confirm you are 18 or older. If you are under 18 and need support, please speak to a trusted adult or contact a service designed for young people such as Childline on 0800 1111. Spill reserves the right to restrict access if there is reason to believe a user is under 18."
            },
          ].map((section, i) => (
            <div key={i} style={{ marginBottom: i < 7 ? "24px" : 0 }}>
              <div style={{ fontSize: "13px", fontWeight: "700", color: "#c8b8a2", marginBottom: "8px", letterSpacing: "0.03em" }}>{section.title}</div>
              <div style={{ fontSize: "13px", color: "rgba(200,184,162,0.55)", lineHeight: "1.75" }}>{section.text}</div>
            </div>
          ))}
        </div>



        {/* Accept button */}
        <button
          onClick={() => scrolled && onAccept()}
          style={{
            width: "100%", padding: "17px",
            background: scrolled ? "rgba(200,184,162,0.88)" : "rgba(200,184,162,0.1)",
            border: "none", borderRadius: "50px",
            color: scrolled ? "#08080e" : "rgba(200,184,162,0.3)",
            fontSize: "15px", fontFamily: "Georgia, serif", fontWeight: "700",
            cursor: scrolled ? "pointer" : "default",
            transition: "all 0.3s", marginBottom: "16px"
          }}>
          I understand and accept
        </button>

        <p style={{ fontSize: "11px", color: "rgba(200,184,162,0.2)", textAlign: "center", lineHeight: "1.6", marginBottom: "8px" }}>
          By accepting you confirm you are 18 or over and agree to these terms.
        </p>
        <p style={{ fontSize: "11px", color: "rgba(200,184,162,0.2)", textAlign: "center", lineHeight: "1.6" }}>
          In crisis? Call Samaritans free: <span style={{ color: "rgba(200,184,162,0.4)" }}>116 123</span>
        </p>
      </div>
    </div>
  );
}

function WelcomeScreen({ onContinue, nuggets, onOpenProfile, onOpenBrotherhood }) {
  const [visible, setVisible] = useState(false);
  const [showNuggets, setShowNuggets] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 150); }, []);

  return (
    <div style={{ minHeight: "100vh", background: "#08080e", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 28px", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: "25%", left: "50%", transform: "translateX(-50%)", width: "320px", height: "320px", background: "radial-gradient(circle, rgba(200,184,162,0.06) 0%, transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", top: "24px", left: "24px", right: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button onClick={onOpenProfile} style={{ background: "rgba(200,184,162,0.08)", border: "1px solid rgba(200,184,162,0.15)", borderRadius: "20px", padding: "8px 14px", color: "rgba(200,184,162,0.6)", fontSize: "12px", cursor: "pointer", fontFamily: "Georgia, serif" }}>👤 Profile</button>
        <div style={{ display: "flex", gap: "8px" }}>
          <button onClick={onOpenBrotherhood} style={{ background: "rgba(200,184,162,0.08)", border: "1px solid rgba(200,184,162,0.15)", borderRadius: "20px", padding: "8px 14px", color: "rgba(200,184,162,0.6)", fontSize: "12px", cursor: "pointer", fontFamily: "Georgia, serif" }}>🤝 Brotherhood</button>
          {nuggets.length > 0 && (
            <button onClick={() => setShowNuggets(true)} style={{ background: "rgba(200,184,162,0.08)", border: "1px solid rgba(200,184,162,0.15)", borderRadius: "20px", padding: "8px 14px", color: "rgba(200,184,162,0.6)", fontSize: "12px", cursor: "pointer", fontFamily: "Georgia, serif" }}>💎 {nuggets.length}</button>
          )}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", zIndex: 1, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(24px)", transition: "all 1.1s cubic-bezier(0.16,1,0.3,1)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "24px" }}>
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
            <path d="M18 14 L46 14 L42 44 L22 44 Z" fill="rgba(200,184,162,0.08)" stroke="rgba(200,184,162,0.5)" strokeWidth="1.5" transform="rotate(-20, 32, 29)"/>
            <path d="M38 34 C42 38 44 46 40 54 C37 58 33 58 31 56" stroke="rgba(200,184,162,0.7)" strokeWidth="2" strokeLinecap="round" fill="none"/>
            <circle cx="43" cy="50" r="2.5" fill="rgba(200,184,162,0.4)"/>
            <circle cx="36" cy="56" r="1.5" fill="rgba(200,184,162,0.3)"/>
            <circle cx="46" cy="44" r="1.5" fill="rgba(200,184,162,0.25)"/>
          </svg>
        </div>
        <div style={{ fontSize: "11px", letterSpacing: "0.45em", color: "rgba(200,184,162,0.35)", marginBottom: "20px", fontFamily: "Georgia, serif" }}>SPILL</div>
        <h1 style={{ fontSize: "34px", fontWeight: "400", color: "#e8e4dc", fontFamily: "Georgia, serif", lineHeight: "1.25", margin: "0 0 18px", letterSpacing: "-0.02em" }}>Do you need<br />to spill it?</h1>
        <p style={{ fontSize: "15px", color: "rgba(200,184,162,0.45)", lineHeight: "1.75", maxWidth: "270px", margin: "0 0 56px", fontFamily: "Georgia, serif" }}>Say what's actually going on.<br />No judgement. No fixing.<br />Just someone who'll listen.</p>
        <button onClick={onContinue} style={{ background: "rgba(200,184,162,0.88)", color: "#08080e", border: "none", borderRadius: "50px", padding: "17px 44px", fontSize: "15px", fontFamily: "Georgia, serif", fontWeight: "700", cursor: "pointer", letterSpacing: "0.04em", marginBottom: "20px" }}>I'm here</button>
        <div style={{ fontSize: "11px", color: "rgba(200,184,162,0.22)", letterSpacing: "0.05em" }}>Private. Free. No agenda.</div>
      </div>
      <div style={{ position: "absolute", bottom: "24px", fontSize: "11px", color: "rgba(200,184,162,0.22)", textAlign: "center" }}>In crisis? Samaritans: <span style={{ color: "rgba(200,184,162,0.45)" }}>116 123</span></div>
      {showNuggets && <NuggetsLibrary nuggets={nuggets} onClose={() => setShowNuggets(false)} />}
    </div>
  );
}

function OnboardingScreen({ onComplete, onBack }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [inputVal, setInputVal] = useState("");
  const [visible, setVisible] = useState(false);
  useEffect(() => { setVisible(false); setTimeout(() => setVisible(true), 100); setInputVal(""); }, [step]);

  const current = ONBOARDING[step];
  const progress = (step / ONBOARDING.length) * 100;

  const goBack = () => {
    if (step === 0) onBack();
    else setStep(step - 1);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#08080e", display: "flex", flexDirection: "column", padding: "48px 24px 40px", position: "relative" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "2px", background: "rgba(200,184,162,0.08)" }}>
        <div style={{ height: "100%", width: `${progress}%`, background: "rgba(200,184,162,0.4)", transition: "width 0.6s ease" }} />
      </div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: "52px" }}>
        <button onClick={goBack} style={{ background: "none", border: "none", color: "rgba(200,184,162,0.4)", cursor: "pointer", fontSize: "22px", lineHeight: 1, marginRight: "12px", padding: 0 }}>‹</button>
        <div style={{ fontSize: "11px", letterSpacing: "0.4em", color: "rgba(200,184,162,0.3)", fontFamily: "Georgia, serif" }}>SPILL</div>
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <div style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(16px)", transition: "all 0.7s ease" }}>
          <div style={{ fontSize: "11px", color: "rgba(200,184,162,0.3)", letterSpacing: "0.15em", marginBottom: "20px", fontFamily: "Georgia, serif" }}>{step + 1} of {ONBOARDING.length}</div>
          <h2 style={{ fontSize: "26px", fontWeight: "400", color: "#e8e4dc", fontFamily: "Georgia, serif", margin: "0 0 32px", lineHeight: "1.35" }}>{current.question}</h2>
          {current.type === "text" && (
            <div>
              <textarea value={inputVal} onChange={e => setInputVal(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (inputVal.trim()) { setAnswers(prev => ({ ...prev, [current.id]: inputVal.trim() })); setStep(step + 1); } } }}
                placeholder={current.placeholder} rows={3} autoFocus
                style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(200,184,162,0.15)", borderRadius: "14px", padding: "16px", color: "#e8e4dc", fontSize: "15px", fontFamily: "Georgia, serif", resize: "none", outline: "none", lineHeight: "1.6", boxSizing: "border-box" }} />
              <button onClick={() => { if (inputVal.trim()) { setAnswers(prev => ({ ...prev, [current.id]: inputVal.trim() })); setStep(step + 1); } }} disabled={!inputVal.trim()}
                style={{ marginTop: "16px", width: "100%", padding: "16px", background: inputVal.trim() ? "rgba(200,184,162,0.88)" : "rgba(200,184,162,0.1)", border: "none", borderRadius: "50px", color: inputVal.trim() ? "#08080e" : "rgba(200,184,162,0.3)", fontSize: "15px", fontFamily: "Georgia, serif", fontWeight: "700", cursor: inputVal.trim() ? "pointer" : "default", transition: "all 0.2s" }}>Continue</button>
            </div>
          )}
          {current.type === "choice" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {current.choices.map((choice, i) => (
                <button key={i} onClick={() => onComplete({ ...answers, [current.id]: choice.label }, MODES[choice.mode])}
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(200,184,162,0.1)", borderRadius: "14px", padding: "18px 20px", textAlign: "left", cursor: "pointer", color: "#e8e4dc", fontFamily: "Georgia, serif", transition: "all 0.2s", display: "flex", alignItems: "center", gap: "14px" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(200,184,162,0.07)"; e.currentTarget.style.borderColor = "rgba(200,184,162,0.25)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.03)"; e.currentTarget.style.borderColor = "rgba(200,184,162,0.1)"; }}>
                  <span style={{ fontSize: "22px" }}>{choice.emoji}</span>
                  <span style={{ fontSize: "16px", color: "#c8b8a2" }}>{choice.label}</span>
                  <span style={{ marginLeft: "auto", color: "rgba(200,184,162,0.25)", fontSize: "18px" }}>›</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <CrisisLine />
    </div>
  );
}


// ── VOICE INPUT ───────────────────────────────────────────────────
function VoiceButton({ onTranscript }) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = "en-GB";

      recognition.onresult = (e) => {
        const transcript = e.results[0][0].transcript;
        onTranscript(transcript);
        setListening(false);
      };

      recognition.onerror = () => setListening(false);
      recognition.onend = () => setListening(false);
      recognitionRef.current = recognition;
    }
  }, []);

  if (!supported) return null;

  const handlePress = () => {
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
    } else {
      try {
        recognitionRef.current?.start();
        setListening(true);
      } catch(e) {
        setListening(false);
      }
    }
  };

  return (
    <button
      onClick={handlePress}
      title={listening ? "Tap to stop" : "Tap to speak"}
      style={{
        width: "44px", height: "44px", borderRadius: "50%", flexShrink: 0,
        background: listening ? "rgba(248,113,113,0.2)" : "rgba(200,184,162,0.08)",
        border: `1px solid ${listening ? "rgba(248,113,113,0.4)" : "rgba(200,184,162,0.15)"}`,
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.2s",
        animation: listening ? "breathePulse 1.5s ease-in-out infinite" : "none",
      }}>
      {listening ? (
        <div style={{ width: "12px", height: "12px", borderRadius: "50%", background: "rgba(248,113,113,0.8)" }} />
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <rect x="9" y="2" width="6" height="12" rx="3" stroke="rgba(200,184,162,0.6)" strokeWidth="2"/>
          <path d="M5 10a7 7 0 0 0 14 0" stroke="rgba(200,184,162,0.6)" strokeWidth="2" strokeLinecap="round"/>
          <line x1="12" y1="17" x2="12" y2="21" stroke="rgba(200,184,162,0.6)" strokeWidth="2" strokeLinecap="round"/>
          <line x1="8" y1="21" x2="16" y2="21" stroke="rgba(200,184,162,0.6)" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      )}
    </button>
  );
}

function ChatScreen({ mode, answers, onReset, onSaveNugget, checkInState, checkInOpening, anonymousId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [nuggetPrompt, setNuggetPrompt] = useState(null);
  const [nuggetText, setNuggetText] = useState("");
  const [savedConfirm, setSavedConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [showFocus, setShowFocus] = useState(true);
  const [showExitNote, setShowExitNote] = useState(false);
  const [showBreathing, setShowBreathing] = useState(false);
  const breathingShown = useRef(false);
  const bottomRef = useRef(null);
  const nuggetSuggested = useRef(false);
  const messageCount = useRef(0);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  useEffect(() => {
    const generateOpening = async () => {
      try {
        const reply = await callGroq([
          { role: "system", content: `${SPILL_SYSTEM_PROMPT}\n\nActive mode: ${mode.label.toUpperCase()} — ${mode.desc}` },
          { role: "user", content: `User shared: mind="${answers.mind}", carrying="${answers.carrying}", need="${answers.need}".${checkInState ? ` They checked in feeling "${checkInState}" today.` : ""} Open with exactly this line and nothing else: "${checkInOpening || "I\'m here. Tell me what\'s going on."}"` }
        ], 150);
        // Natural opening delay
        await new Promise(resolve => setTimeout(resolve, 1200));
        setMessages([{ role: "assistant", content: reply }]);
      } catch {
        setMessages([{ role: "assistant", content: "I'm here. Tell me what's going on." }]);
      } finally { setLoading(false); }
    };
    generateOpening();
  }, []);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || loading || sending) return;
    const shouldSuggest = !nuggetSuggested.current && detectNuggetMoment(text, messageCount.current);
    const shouldBreathe = !breathingShown.current && messageCount.current >= 1 && detectBreathingMoment(text);
    const newMessages = [...messages, { role: "user", content: text }];
    setMessages(newMessages); setInput(""); setSending(true); setError(null);
    messageCount.current += 1;
    try {
      const trimmed = trimHistory(newMessages);
      const reply = await callGroq([
        { role: "system", content: `${SPILL_SYSTEM_PROMPT}\n\nUser context: mind="${answers.mind}", carrying="${answers.carrying}", need="${answers.need}"${checkInState ? `\nCheck-in state: The user said they are feeling "${checkInState}" today. Keep this in mind throughout the conversation — reference it naturally when relevant, not in every message.` : ""}\nActive mode: ${mode.label.toUpperCase()} — ${mode.desc}` },
        ...trimmed.map(m => ({ role: m.role, content: m.content }))
      ]);
      // Natural thinking delay — makes Spill feel considered, not instant
      const thinkingTime = Math.min(1000 + reply.length * 2, 4000);
      await new Promise(resolve => setTimeout(resolve, thinkingTime));
      setMessages(prev => [...prev, { role: "assistant", content: reply }]);
      if (shouldSuggest) {
        nuggetSuggested.current = true;
        setTimeout(() => { setNuggetText(text); setNuggetPrompt(true); }, 1200);
      }
      if (shouldBreathe) {
        breathingShown.current = true;
        setTimeout(() => setShowBreathing(true), 800);
      }
    } catch {
      setError("Something went quiet. Tap to try again.");
      setMessages(prev => prev.slice(0, -1));
      setInput(text);
    } finally { setSending(false); }
  }, [input, loading, sending, messages, answers, mode]);

  const handleSaveNugget = (text, category) => {
    onSaveNugget({ text, category, strength: 0, respect: 0, timestamp: Date.now() });
    setNuggetPrompt(null); setSavedConfirm(true); setTimeout(() => setSavedConfirm(false), 3000);
  };

  const isDisabled = !input.trim() || loading || sending;

  return (
    <div style={{ minHeight: "100vh", background: "#08080e", display: "flex", flexDirection: "column", fontFamily: "Georgia, serif", color: "#e8e4dc" }}>
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "radial-gradient(ellipse at 15% 15%, rgba(30,40,80,0.25) 0%, transparent 55%)", pointerEvents: "none", zIndex: 0 }} />
      {showFocus && <FocusMode onDismiss={() => setShowFocus(false)} />}
      <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "rgba(8,8,14,0.96)", backdropFilter: "blur(12px)", borderBottom: "1px solid rgba(255,255,255,0.05)", zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <button onClick={() => messages.length > 1 ? setShowExitNote(true) : onReset()} style={{ background: "none", border: "none", color: "rgba(200,184,162,0.4)", cursor: "pointer", fontSize: "22px", padding: "0 10px 0 0", lineHeight: 1 }}>‹</button>
          <div>
            <div style={{ fontSize: "15px", fontWeight: "600", color: "#c8b8a2", letterSpacing: "0.08em" }}>SPILL</div>
            <div style={{ fontSize: "11px", color: "rgba(200,184,162,0.38)", display: "flex", alignItems: "center", gap: "4px" }}><span>{mode.emoji}</span><span>{mode.label} Mode</span></div>
          </div>
        </div>
        <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: (loading || sending) ? "#facc15" : "#4ade80", boxShadow: (loading || sending) ? "0 0 8px rgba(250,204,21,0.5)" : "0 0 8px rgba(74,222,128,0.5)", transition: "all 0.3s" }} />
      </div>
      {savedConfirm && <div style={{ position: "fixed", top: "80px", left: "50%", transform: "translateX(-50%)", background: "rgba(74,222,128,0.12)", border: "1px solid rgba(74,222,128,0.25)", borderRadius: "20px", padding: "10px 20px", fontSize: "13px", color: "#4ade80", fontFamily: "Georgia, serif", zIndex: 20, whiteSpace: "nowrap" }}>💎 Saved — someone will find this when they need it</div>}
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 16px", display: "flex", flexDirection: "column", gap: "14px", zIndex: 1 }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: msg.role === "user" ? "flex-end" : "flex-start", animation: "fadeUp 0.3s ease forwards" }}>
            <div style={{ maxWidth: "82%", padding: "12px 16px", borderRadius: msg.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px", background: msg.role === "user" ? "rgba(200,184,162,0.1)" : "rgba(255,255,255,0.04)", border: msg.role === "user" ? "1px solid rgba(200,184,162,0.18)" : "1px solid rgba(255,255,255,0.06)", fontSize: "15px", lineHeight: "1.65", color: msg.role === "user" ? "#e8e4dc" : "#c8b8a2", whiteSpace: "pre-wrap" }}>{msg.content}</div>
            {i > 0 && <button onClick={() => { setNuggetText(msg.content); setNuggetPrompt(true); }} style={{ marginTop: "4px", background: "none", border: "none", color: "rgba(200,184,162,0.2)", fontSize: "11px", cursor: "pointer", fontFamily: "Georgia, serif", padding: "2px 4px", transition: "color 0.2s" }} onMouseEnter={e => e.target.style.color = "rgba(200,184,162,0.55)"} onMouseLeave={e => e.target.style.color = "rgba(200,184,162,0.2)"}>💎 save as nugget</button>}
          </div>
        ))}
        {error && (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <button onClick={() => { setError(null); sendMessage(); }} style={{ background: "rgba(200,184,162,0.06)", border: "1px solid rgba(200,184,162,0.15)", borderRadius: "20px", padding: "10px 20px", color: "rgba(200,184,162,0.5)", fontSize: "13px", fontFamily: "Georgia, serif", cursor: "pointer" }}>{error} ↺</button>
          </div>
        )}
        {(loading || sending) && (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div style={{ padding: "14px 18px", borderRadius: "18px 18px 18px 4px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)", display: "flex", gap: "6px", alignItems: "center" }}>
              {[0,1,2].map(i => <div key={i} style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#c8b8a2", opacity: 0.4, animation: `pulse 1.2s ease-in-out ${i*0.2}s infinite` }} />)}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      <CrisisLine />
      <div style={{ padding: "12px 16px 28px", background: "rgba(8,8,14,0.98)", borderTop: "1px solid rgba(255,255,255,0.05)", display: "flex", gap: "10px", alignItems: "flex-end", zIndex: 10 }}>
        <textarea value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Say it how it is..." rows={1}
          style={{ flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(200,184,162,0.12)", borderRadius: "16px", padding: "12px 16px", color: "#e8e4dc", fontSize: "15px", fontFamily: "Georgia, serif", resize: "none", outline: "none", lineHeight: "1.5", maxHeight: "120px", overflowY: "auto" }}
          onInput={e => { e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px"; }} />
        <VoiceButton onTranscript={text => setInput(prev => prev ? prev + " " + text : text)} />
        <button onClick={sendMessage} disabled={isDisabled}
          style={{ width: "44px", height: "44px", borderRadius: "50%", background: !isDisabled ? "rgba(200,184,162,0.88)" : "rgba(200,184,162,0.1)", border: "none", cursor: !isDisabled ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s", flexShrink: 0 }}>
          {sending ? (
            <div style={{ width: "16px", height: "16px", border: "2px solid rgba(8,8,14,0.3)", borderTop: "2px solid #08080e", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13M22 2L15 22L11 13M11 13L2 9L22 2" stroke={!isDisabled ? "#08080e" : "rgba(200,184,162,0.3)"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          )}
        </button>
      </div>
      {nuggetPrompt && <SaveNuggetModal text={nuggetText} onSave={handleSaveNugget} onDismiss={() => setNuggetPrompt(null)} />}
      {showBreathing && <BreathingSpace onComplete={() => setShowBreathing(false)} />}
      {showExitNote && (
        <ExitNoteModal
          onSave={async (note) => {
            try { await db.saveExitNote(anonymousId, note); } catch(e) {}
            setShowExitNote(false);
            onReset();
          }}
          onSkip={() => { setShowExitNote(false); onReset(); }}
        />
      )}
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        @keyframes pulse { 0%,100% { opacity:0.3; transform:scale(0.8); } 50% { opacity:1; transform:scale(1.2); } }
        @keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
        @keyframes breathePulse { 0%,100% { box-shadow: 0 0 0 0 rgba(248,113,113,0.3); } 50% { box-shadow: 0 0 0 8px rgba(248,113,113,0); } }
        * { box-sizing: border-box; }
        textarea::placeholder { color: rgba(200,184,162,0.25); }
        ::-webkit-scrollbar { width: 0; }
      `}</style>
    </div>
  );
}

export default function App() {
  const [screen, setScreen] = useState("loading");
  const [selectedMode, setSelectedMode] = useState(null);
  const [onboardingAnswers, setOnboardingAnswers] = useState({});
  const [nuggets, setNuggets] = useState([]);
  const [allNuggets, setAllNuggets] = useState([]);
  const [profile, setProfile] = useState({ handle: "", joinDate: Date.now() });
  const [conversationCount, setConversationCount] = useState(0);
  const [selectedCircle, setSelectedCircle] = useState(null);
  const [isReturningUser, setIsReturningUser] = useState(false);
  const [checkInState, setCheckInState] = useState(null);
  const [checkInOpening, setCheckInOpening] = useState(null);
  const anonymousId = getAnonymousId();

  // Load user data from Supabase on mount
  useEffect(() => {
    const init = async () => {
      const hasAccepted = localStorage.getItem("spill_terms_accepted");
      const hasOnboarded = localStorage.getItem("spill_onboarded");
      const stored = localStorage.getItem("spill_last_visit");

      // First time — show disclaimer
      if (!hasAccepted) {
        setScreen("disclaimer");
        return;
      }

      try {
        // Load profile from Supabase
        const dbProfile = await db.getProfile(anonymousId);
        if (dbProfile) {
          setProfile({ handle: dbProfile.handle || "Anonymous", joinDate: dbProfile.join_date || Date.now() });
          setConversationCount(dbProfile.conversation_count || 0);
        }

        // Load user's own nuggets
        const dbNuggets = await db.getNuggets(anonymousId);
        if (dbNuggets && dbNuggets.length) {
          setNuggets(dbNuggets.map(n => ({ text: n.text, category: n.category, strength: n.strength || 0, respect: n.respect || 0, timestamp: new Date(n.created_at).getTime() })));
        }

        // Load all nuggets for Brotherhood circles
        const dbAllNuggets = await db.getAllNuggets();
        if (dbAllNuggets && dbAllNuggets.length) {
          setAllNuggets(dbAllNuggets.map(n => ({ text: n.text, category: n.category, strength: n.strength || 0, respect: n.respect || 0, timestamp: new Date(n.created_at).getTime() })));
        }
      } catch (e) {
        console.log("Supabase load error:", e);
      }

      // Returning user after 24hrs — show check-in
      if (stored && hasOnboarded) {
        const hoursSince = (Date.now() - parseInt(stored)) / (1000 * 60 * 60);
        if (hoursSince >= 24) {
          setIsReturningUser(true);
          localStorage.setItem("spill_last_visit", Date.now().toString());
          setScreen("checkin");
          return;
        }
      }

      localStorage.setItem("spill_last_visit", Date.now().toString());
      setScreen("welcome");
    };
    init();
  }, []);

  // Save nugget to Supabase
  const handleSaveNugget = async (nugget) => {
    const newNugget = { ...nugget, timestamp: Date.now(), category: nugget.category.toLowerCase().trim() };
    setNuggets(prev => [newNugget, ...prev]);
    setAllNuggets(prev => [newNugget, ...prev]);
    try {
      const result = await db.saveNugget(anonymousId, newNugget);
      console.log("Nugget saved:", result);
    } catch (e) {
      console.error("Save nugget error:", e);
    }
  };

  // Update handle in Supabase
  const handleUpdateHandle = async (handle) => {
    setProfile(prev => ({ ...prev, handle }));
    try {
      await db.upsertProfile(anonymousId, { handle, join_date: profile.joinDate });
    } catch (e) {
      console.log("Update handle error:", e);
    }
  };

  // Increment conversation count
  const handleNewConversation = async () => {
    const newCount = conversationCount + 1;
    setConversationCount(newCount);
    try {
      await db.upsertProfile(anonymousId, {
        handle: profile.handle,
        join_date: profile.joinDate || Date.now(),
        conversation_count: newCount
      });
    } catch (e) {
      console.log("Conversation count error:", e);
    }
  };

  // Analytics route
  if (window.location.pathname === '/analytics') {
    const Analytics = require('./Analytics').default;
    return <Analytics />;
  }

  return (
    <div style={{ maxWidth: "480px", margin: "0 auto" }}>
      {screen === "loading" && <LoadingScreen />}
      {screen === "disclaimer" && <DisclaimerScreen onAccept={() => {
        localStorage.setItem("spill_terms_accepted", "true");
        localStorage.setItem("spill_last_visit", Date.now().toString());
        setScreen("welcome");
      }} />}
      {screen === "checkin" && <CheckInScreen onComplete={async (option) => {
        setCheckInState(option.key);
        setCheckInOpening(option.opening);
        if (!onboardingAnswers.mind) {
          setOnboardingAnswers({ mind: "things on my mind", carrying: "the people I care about", need: "to talk" });
        }
        const modeMap = { heavy: MODES.vent, okay: MODES.reflect, good: MODES.support };
        setSelectedMode(modeMap[option.key] || MODES.vent);
        await handleNewConversation();
        try { await db.saveCheckin(anonymousId, option.key); } catch(e) {}
        localStorage.setItem("spill_last_visit", Date.now().toString());
        setScreen("chat");
      }} />}
      {screen === "welcome" && <WelcomeScreen onContinue={() => setScreen("onboarding")} nuggets={nuggets} onOpenProfile={() => setScreen("profile")} onOpenBrotherhood={() => setScreen("brotherhood")} />}
      {screen === "onboarding" && <OnboardingScreen onComplete={async (answers, mode) => {
        setOnboardingAnswers(answers);
        setSelectedMode(mode);
        await handleNewConversation();
        try {
          await db.upsertProfile(anonymousId, { handle: "Anonymous", join_date: Date.now(), conversation_count: conversationCount + 1 });
        } catch(e) {}
        localStorage.setItem("spill_onboarded", "true");
        localStorage.setItem("spill_last_visit", Date.now().toString());
        setScreen("chat");
      }} onBack={() => setScreen("welcome")} />}
      {screen === "chat" && selectedMode && <ChatScreen mode={selectedMode} answers={onboardingAnswers} onReset={() => { setScreen("welcome"); setCheckInState(null); setCheckInOpening(null); }} onSaveNugget={handleSaveNugget} checkInState={checkInState} checkInOpening={checkInOpening} anonymousId={anonymousId} />}
      {screen === "profile" && <ProfileScreen profile={profile} nuggets={nuggets} conversationCount={conversationCount} onUpdateHandle={handleUpdateHandle} onClose={() => setScreen("welcome")} onOpenBrotherhood={() => setScreen("brotherhood")} />}
      {screen === "brotherhood" && !selectedCircle && <BrotherhoodScreen nuggets={allNuggets} onSelectCircle={c => { setSelectedCircle(c); setScreen("circle"); }} onClose={() => setScreen("welcome")} />}
      {screen === "circle" && selectedCircle && <CircleFeedScreen circle={selectedCircle} nuggets={allNuggets} onClose={() => { setSelectedCircle(null); setScreen("brotherhood"); }} />}
    </div>
  );
}
